'use es6';

import Actions from 'AdsUI/lib/Actions';
import { List, Set as ImmutableSet } from 'immutable';
import { MAX_SIMULTANEOUS_ACCOUNTS, Networks, EntityStatusMap, AttributionViews, CUSTOM_IDS, USERS_AND_TEAMS_TYPE } from 'AdsUI/lib/Constants';
import AdsSafeStorage from 'AdsUI/lib/AdsSafeStorage';
import { LEGACY_AD_ACCOUNTS_FILTER, GLOBAL_FILTERS } from 'AdsUI/lib/StorageKeys';
import { hasDemoScope, hasUsersAndTeams } from 'AdsUI/selectors/AuthSelectors';
import { getAdAccounts } from 'AdsUI/selectors/AccountsSelectors';
import { getAdAccountsByUserAndTeams } from 'AdsUI/selectors/GlobalFilters';
import { getBiglyticsTimeRange } from 'AdsUI/lib/Utils';
import { fromMoment, toMoment } from 'UIComponents/core/SimpleDate';
import { getDateRangePresetForString, PRESET_TYPES } from '../lib/AdsSimpleDateRange';
import I18n from 'I18n';
import { getFiltersFromQuery } from 'AdsUI/lib/GlobalFiltersQueryParams';
import AdAccountIdentityRecord from 'AdsUI/records/ad-accounts/AdAccountIdentityRecord';
import { AdAccountStatus } from 'AdsUI/records/ad-accounts/AdAccountRecord';

const getDefaults = adAccounts => {
  // We want to make sure at least one of each connected network is chosen by default
  const accessibleAccounts = adAccounts.filter(account => account.accessibleToPrimaryUser);
  const firstAdAccountByNetwork = Object.keys(Networks).map(network => accessibleAccounts.find(account => account.network === network)).filter(Boolean);
  const remainingAccounts = accessibleAccounts.filter(account => firstAdAccountByNetwork.indexOf(account) === -1);
  return List().concat(firstAdAccountByNetwork, remainingAccounts).take(MAX_SIMULTANEOUS_ACCOUNTS).map(account => new AdAccountIdentityRecord({
    accountId: account.accountId,
    network: account.network
  })).toSet();
};

const loadTimeRange = ({
  type,
  endDate,
  startDate
}) => {
  if (type !== PRESET_TYPES.CUSTOM) {
    return getDateRangePresetForString(type);
  }

  if (!startDate || !endDate) {
    return getDateRangePresetForString(PRESET_TYPES.LAST_THIRTY_DAYS);
  }

  return getDateRangePresetForString(PRESET_TYPES.CUSTOM, fromMoment(I18n.moment(startDate)), fromMoment(I18n.moment(endDate)));
};

const dehydrateFilters = globalFilters => {
  return {
    usersAndTeams: globalFilters.usersAndTeams.toJS(),
    attributionView: globalFilters.attributionView,
    adAccounts: globalFilters.adAccounts.toJS(),
    statuses: globalFilters.statuses.toJS(),
    timeRange: {
      type: globalFilters.timeRange.type,
      startDate: toMoment(globalFilters.timeRange.startDate).valueOf(),
      endDate: toMoment(globalFilters.timeRange.endDate).valueOf()
    }
  };
};

const hydrateFilters = adAccounts => {
  const queryFilters = getFiltersFromQuery(window.location.search || '');
  const dehydratedFilters = JSON.parse(AdsSafeStorage.getItem(GLOBAL_FILTERS) || '{}');
  const legacyAdAccounts = JSON.parse(AdsSafeStorage.getItem(LEGACY_AD_ACCOUNTS_FILTER) || 'null');
  const storedAdAccounts = ImmutableSet((queryFilters.adAccounts || dehydratedFilters.adAccounts || legacyAdAccounts || []).filter(({
    accountId,
    network
  }) => {
    const adAccount = adAccounts.find(account => account.accountId === accountId && account.network === network);

    if (!adAccount) {
      return false;
    }

    return adAccount.accessibleToPrimaryUser && adAccount.status !== AdAccountStatus.ON_HOLD;
  }).map(account => new AdAccountIdentityRecord(account)));
  const usersAndTeams = queryFilters.usersAndTeams || dehydratedFilters.usersAndTeams || {
    id: CUSTOM_IDS.ALL,
    type: USERS_AND_TEAMS_TYPE.CUSTOM
  };
  return {
    usersAndTeams,
    attributionView: queryFilters.attributionView || dehydratedFilters.attributionView || AttributionViews.FIRST_FORM_SUBMISSION,
    statuses: ImmutableSet(queryFilters.statuses || dehydratedFilters.statuses || [EntityStatusMap.ENABLED, EntityStatusMap.PAUSED]),
    adAccounts: storedAdAccounts.size || usersAndTeams.id !== CUSTOM_IDS.ALL ? storedAdAccounts : getDefaults(adAccounts),
    timeRange: queryFilters.timeRange || (dehydratedFilters.timeRange ? loadTimeRange(dehydratedFilters.timeRange) : getDateRangePresetForString(PRESET_TYPES.LAST_THIRTY_DAYS)),
    initialized: true
  };
};

export const initialize = () => (dispatch, getState) => {
  const state = getState();
  const adAccounts = hasUsersAndTeams(state) ? getAdAccountsByUserAndTeams(state) : getAdAccounts(state);

  if (hasDemoScope(state)) {
    return dispatch({
      type: Actions.GLOBAL_FILTER_CHANGE,
      payload: {
        usersAndTeams: {
          id: CUSTOM_IDS.ALL,
          type: USERS_AND_TEAMS_TYPE.CUSTOM
        },
        adAccounts: getDefaults(adAccounts),
        statuses: ImmutableSet.of(EntityStatusMap.ENABLED),
        timeRange: getBiglyticsTimeRange(),
        initialized: true
      }
    });
  }

  const hydratedFilters = hydrateFilters(adAccounts);
  const dehydratedFilters = JSON.parse(AdsSafeStorage.getItem(GLOBAL_FILTERS) || '{}');

  if (dehydratedFilters.adAccounts) {
    const hydratedFiltersAccounts = new Set(hydratedFilters.adAccounts.map(a => a.accountId).toJS());

    if (dehydratedFilters.adAccounts.length !== hydratedFiltersAccounts.size) {
      dehydratedFilters.adAccounts = dehydratedFilters.adAccounts.filter(({
        accountId
      }) => hydratedFiltersAccounts.has(accountId));
      AdsSafeStorage.setItem(GLOBAL_FILTERS, JSON.stringify(dehydratedFilters));
    }
  }

  return dispatch({
    type: Actions.GLOBAL_FILTER_CHANGE,
    payload: hydratedFilters
  });
};
export const changeFilters = newFilters => (dispatch, getState) => {
  if (newFilters.adAccounts) {
    newFilters.adAccounts = newFilters.adAccounts.map(account => new AdAccountIdentityRecord({
      accountId: account.accountId,
      network: account.network,
      adAccountAssignees: account.adAccountAssignees
    }));
  }

  dispatch({
    type: Actions.GLOBAL_FILTER_CHANGE,
    payload: newFilters
  });
  const state = getState();

  if (!hasDemoScope(state)) {
    const {
      globalFilters
    } = state;
    AdsSafeStorage.setItem(GLOBAL_FILTERS, JSON.stringify(dehydrateFilters(globalFilters.merge(newFilters))));
  }

  if (newFilters.usersAndTeams) {
    const adAccounts = getAdAccountsByUserAndTeams(state);
    const filteredSelectedAccounts = state.globalFilters.adAccounts.filter(account => adAccounts.find(a => a.accountId === account.accountId && a.network === account.network));
    dispatch({
      type: Actions.GLOBAL_FILTER_CHANGE,
      payload: {
        adAccounts: filteredSelectedAccounts
      }
    });
  }
};
export const resetFilters = () => (dispatch, getState) => dispatch({
  type: Actions.GLOBAL_FILTER_CHANGE,
  payload: {
    attributionView: AttributionViews.FIRST_FORM_SUBMISSION,
    statuses: ImmutableSet([EntityStatusMap.ENABLED, EntityStatusMap.PAUSED]),
    adAccounts: getDefaults(getAdAccountsByUserAndTeams(getState())).map(account => new AdAccountIdentityRecord({
      accountId: account.accountId,
      network: account.network
    })),
    timeRange: getDateRangePresetForString(PRESET_TYPES.LAST_THIRTY_DAYS),
    initialized: true
  }
});